import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Minus, MoreHorizontal, Plus } from 'lucide-react';
import { IMAGE_TAG_COLORS, type ImageTag, type ImageTagColor } from '../../services/api/imageTagService';
import { TaskyButton, TaskyIconButton } from '../tasky';
import { IMAGE_TAG_COLOR_LABELS, tagColorClasses } from './imageTagColors';

export type TagCheckState = 'all' | 'some' | 'none';

interface ImageTagPickerProps {
  tags: ImageTag[];
  /** 対象のバナー（1枚または選択中の複数枚）に、そのタグが付いているか */
  checkState: (tagId: number) => TagCheckState;
  onToggle: (tag: ImageTag, attach: boolean) => void;
  /** 作って対象に付ける */
  onCreate: (name: string) => void;
  onUpdate: (tag: ImageTag, changes: { name?: string; color?: ImageTagColor }) => void;
  onDelete: (tag: ImageTag) => void;
}

export const MAX_TAG_NAME_LENGTH = 30;

const CheckMark: React.FC<{ state: TagCheckState }> = ({ state }) => (
  <span
    aria-hidden
    className={`flex h-4 w-4 flex-none items-center justify-center rounded border-2 ${
      state === 'none' ? 'border-gray-300 bg-white' : 'border-primary bg-primary text-white'
    }`}
  >
    {state === 'all' && <Check className="h-3 w-3" strokeWidth={3} />}
    {state === 'some' && <Minus className="h-3 w-3" strokeWidth={3} />}
  </span>
);

/** 1つのタグの名前・色の変更と削除。タグを付けるポップアップと、一覧の上のタグの行の両方から開く */
export const TagEditor: React.FC<{
  tag: ImageTag;
  onSave: (changes: { name?: string; color?: ImageTagColor }) => void;
  onDelete: () => void;
  onDone: () => void;
}> = ({ tag, onSave, onDelete, onDone }) => {
  const [name, setName] = useState(tag.name);
  // Enter・blur・閉じたときのどれでも呼ばれるので、同じ名前を二度送らない
  const savedNameRef = useRef(tag.name);
  const commitName = () => {
    const trimmed = name.trim();
    if (trimmed && trimmed !== savedNameRef.current) {
      savedNameRef.current = trimmed;
      onSave({ name: trimmed });
    }
  };
  // ポップアップの外を押して閉じたときも、書き換えた名前を残す。Esc と削除のときは残さない
  const commitOnCloseRef = useRef(commitName);
  commitOnCloseRef.current = commitName;
  const discardRef = useRef(false);
  useEffect(() => () => {
    if (!discardRef.current) commitOnCloseRef.current();
  }, []);

  return (
    <div className="space-y-2 rounded-md bg-warm-surface p-2">
      <input
        aria-label="タグ名"
        value={name}
        maxLength={MAX_TAG_NAME_LENGTH}
        autoFocus
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
            event.preventDefault();
            commitName();
            onDone();
          } else if (event.key === 'Escape') {
            discardRef.current = true;
          }
        }}
        onBlur={() => {
          if (!discardRef.current) commitName();
        }}
        className="w-full rounded-md border border-warm-border bg-white px-2 py-1.5 text-sm focus:border-primary focus:outline-none"
      />
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="色">
        {IMAGE_TAG_COLORS.map((color) => (
          <TaskyIconButton
            key={color}
            type="button"
            size="sm"
            role="radio"
            aria-checked={tag.color === color}
            aria-label={IMAGE_TAG_COLOR_LABELS[color]}
            onClick={() => onSave({ color })}
            className={`h-6 w-6 [&_svg]:size-3.5 ${tagColorClasses(color).swatch} ${
              tag.color === color ? 'ring-2 ring-offset-1 ring-dark' : ''
            }`}
          >
            {tag.color === color && <Check strokeWidth={3} />}
          </TaskyIconButton>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <TaskyButton
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            discardRef.current = true;
            onDelete();
          }}
          className="h-auto px-1.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 hover:text-red-700"
        >
          削除
        </TaskyButton>
        <TaskyButton
          type="button"
          variant="ghost"
          size="sm"
          onClick={onDone}
          className="h-auto px-2 py-1 text-xs font-semibold text-primary hover:bg-tk-primary-tint hover:text-primary"
        >
          完了
        </TaskyButton>
      </div>
    </div>
  );
};

const ImageTagPicker: React.FC<ImageTagPickerProps> = ({ tags, checkState, onToggle, onCreate, onUpdate, onDelete }) => {
  const [query, setQuery] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const trimmed = query.trim();
  const visibleTags = useMemo(() => {
    const needle = trimmed.toLowerCase();
    return needle ? tags.filter((tag) => tag.name.toLowerCase().includes(needle)) : tags;
  }, [tags, trimmed]);
  // 同じ名前かはサーバー（utf8mb4_bin）と同じく文字どおりに比べる。探すときだけ大文字小文字を区別しない
  const exactMatch = tags.find((tag) => tag.name === trimmed);
  const canCreate = trimmed.length > 0 && !exactMatch;

  const toggle = (tag: ImageTag) => onToggle(tag, checkState(tag.id) !== 'all');

  const submit = () => {
    if (exactMatch) {
      toggle(exactMatch);
    } else if (canCreate) {
      onCreate(trimmed);
    }
    setQuery('');
  };

  return (
    <div className="space-y-1">
      <input
        aria-label="タグを検索・作成"
        placeholder="タグを入力"
        value={query}
        maxLength={MAX_TAG_NAME_LENGTH}
        autoFocus
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          // 日本語入力の確定の Enter では作らない
          if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
            event.preventDefault();
            submit();
          }
        }}
        className="mb-1 w-full rounded-md border border-warm-border px-2.5 py-2 text-sm focus:border-primary focus:outline-none"
      />
      <ul className="max-h-64 overflow-y-auto" aria-label="タグ">
        {visibleTags.map((tag) => (
          <li key={tag.id}>
            {editingId === tag.id ? (
              <TagEditor
                tag={tag}
                onSave={(changes) => onUpdate(tag, changes)}
                onDelete={() => {
                  setEditingId(null);
                  onDelete(tag);
                }}
                onDone={() => setEditingId(null)}
              />
            ) : (
              <div className="group flex items-center rounded-md hover:bg-warm-hover">
                <TaskyButton
                  type="button"
                  variant="ghost"
                  size="sm"
                  role="checkbox"
                  aria-checked={checkState(tag.id) === 'all' ? true : checkState(tag.id) === 'some' ? 'mixed' : false}
                  onClick={() => toggle(tag)}
                  className="h-auto min-w-0 flex-1 justify-start px-2 py-1.5 text-left font-normal text-dark hover:bg-transparent"
                >
                  <CheckMark state={checkState(tag.id)} />
                  <span className={`h-2 w-2 flex-none rounded-full ${tagColorClasses(tag.color).dot}`} aria-hidden />
                  <span className="truncate">{tag.name}</span>
                </TaskyButton>
                <TaskyIconButton
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`${tag.name}を編集`}
                  onClick={() => setEditingId(tag.id)}
                  className="mr-1 h-6 w-6 flex-none rounded text-gray-400 opacity-0 hover:bg-white hover:text-gray-700 focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
                >
                  <MoreHorizontal />
                </TaskyIconButton>
              </div>
            )}
          </li>
        ))}
      </ul>
      {canCreate && (
        <TaskyButton
          type="button"
          variant="ghost"
          size="sm"
          onClick={submit}
          className="h-auto w-full justify-start rounded-md px-2 py-1.5 text-left font-semibold text-primary hover:bg-tk-primary-tint hover:text-primary"
        >
          <Plus />
          <span className="truncate">「{trimmed}」を作成</span>
        </TaskyButton>
      )}
    </div>
  );
};

export default ImageTagPicker;
